import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { adminService, type ProductCategoryOption } from '@/services/adminService';
import { handleApiError } from '@/services/api';

const { Title, Text } = Typography;

type FormMode = 'create' | 'edit';

interface CategoryFormValues {
  Category: string;
  CategoryDescription: string;
}

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<ProductCategoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CategoryFormValues>();

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllCategories();
      setCategories(data);
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openCreate = () => {
    setFormMode('create');
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: ProductCategoryOption) => {
    setFormMode('edit');
    form.setFieldsValue({
      Category: record.Category,
      CategoryDescription: record.CategoryDescription,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      if (formMode === 'create') {
        await adminService.createCategory(values.Category.trim(), values.CategoryDescription.trim());
        message.success(`Category "${values.Category}" created`);
      } else {
        await adminService.updateCategory(values.Category, values.CategoryDescription.trim());
        message.success(`Category "${values.Category}" updated`);
      }
      closeModal();
      await loadCategories();
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record: ProductCategoryOption) => {
    try {
      await adminService.deleteCategory(record.Category);
      message.success(`Category "${record.Category}" deleted`);
      await loadCategories();
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to delete category');
    }
  };

  const columns: ColumnsType<ProductCategoryOption> = [
    {
      title: 'Code',
      dataIndex: 'Category',
      key: 'Category',
      width: 140,
      sorter: (a, b) => a.Category.localeCompare(b.Category),
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'CategoryDescription',
      key: 'CategoryDescription',
      sorter: (a, b) => a.CategoryDescription.localeCompare(b.CategoryDescription),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit description">
            <Button icon={<EditOutlined />} onClick={() => openEdit(record)}>
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete this category?"
            description="Categories in use by products cannot be deleted."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <AppstoreOutlined style={{ fontSize: 24, color: '#ffae00' }} />
            <Title level={3} style={{ margin: 0 }}>
              Category Management
            </Title>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadCategories} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add Category
            </Button>
          </Space>
        </Space>

        <Text type="secondary">
          Categories shown here populate the Category dropdown on the Add/Edit Product form.
          The category code is the identifier; description is what customers and admins see.
        </Text>

        <Table<ProductCategoryOption>
          rowKey="Category"
          columns={columns}
          dataSource={categories}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          style={{ marginTop: 16 }}
        />
      </Card>

      <Modal
        title={formMode === 'create' ? 'Add Category' : 'Edit Category'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={formMode === 'create' ? 'Create' : 'Save Changes'}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="Category"
            label="Code"
            extra={
              formMode === 'edit'
                ? 'Category code is the identifier and cannot be changed after creation.'
                : 'Short code, max 5 characters (e.g. NOOD, FRSH, PCKG).'
            }
            rules={[
              { required: true, message: 'Please enter a category code' },
              { max: 5, message: 'Code must be 5 characters or less' },
              {
                pattern: /^[a-zA-Z0-9_\- ]+$/,
                message: 'Only letters, digits, spaces, underscores, and hyphens',
              },
            ]}
          >
            <Input disabled={formMode === 'edit'} maxLength={5} />
          </Form.Item>
          <Form.Item
            name="CategoryDescription"
            label="Description"
            rules={[
              { required: true, message: 'Please enter a description' },
              { max: 100, message: 'Description must be 100 characters or less' },
            ]}
          >
            <Input maxLength={100} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
